import React from 'react';
import { useQueries } from '@tanstack/react-query';
import type { AnnualTimetableCell } from '../types/domain';
import { buildAnnualCellMap } from '../utils/annualTimetable';
import { resolveMergedTimetableCellsByKey } from '../utils/timetableCellsByKeyResolve';
import { annualTimetableCellsQueryOptions } from '../query/hooks/useAnnualTimetableCellsQuery';
import { annualTimetableWeekMemosQueryOptions } from '../query/hooks/useAnnualTimetableWeekMemosQuery';

export type AnnualTimetableWeekMemoState = {
  id?: string;
  activityLines: string[];
  generalMemo: string;
};

export type UseAnnualTimetablePageQueriesParams = {
  workspaceId: string;
  academicYear: number;
  gradeIds: string[];
  isActive: boolean;
  schoolScopedCacheSyncReady: boolean;
};

export type UseAnnualTimetablePageQueriesResult = {
  scopeKey: string;
  cellsByKey: Record<string, AnnualTimetableCell>;
  weekMemosByKey: Record<string, AnnualTimetableWeekMemoState>;
  loading: boolean;
  isReady: boolean;
  error: string | null;
};

function weekMemoStateKey(termNumber: 1 | 2, gradeId: string, mondayIso: string): string {
  return `${termNumber}|${gradeId}|${mondayIso}`;
}

function buildWeekMemosStateMap(
  term1Rows: { term_number: 1 | 2; grade_id: string; monday_iso: string; id?: string; activity_lines?: string[]; general_memo?: string | null }[],
  term2Rows: typeof term1Rows
): Record<string, AnnualTimetableWeekMemoState> {
  const next: Record<string, AnnualTimetableWeekMemoState> = {};
  for (const row of [...term1Rows, ...term2Rows]) {
    const k = weekMemoStateKey(row.term_number, row.grade_id, row.monday_iso);
    const lines = Array.isArray(row.activity_lines)
      ? row.activity_lines.map((x) => (typeof x === 'string' ? x : String(x ?? '')))
      : [];
    next[k] = {
      id: row.id,
      activityLines: lines,
      generalMemo: row.general_memo ?? '',
    };
  }
  return next;
}

export function useAnnualTimetablePageQueries(
  params: UseAnnualTimetablePageQueriesParams
): UseAnnualTimetablePageQueriesResult {
  const { workspaceId, academicYear, gradeIds, isActive, schoolScopedCacheSyncReady } = params;
  const ids = React.useMemo(() => gradeIds.filter(Boolean), [gradeIds]);
  const queryEnabled = isActive && schoolScopedCacheSyncReady && !!workspaceId.trim();

  const scopeKey = React.useMemo(() => {
    if (!workspaceId || ids.length === 0) return '';
    return `${workspaceId}|${academicYear}|${ids.join(',')}`;
  }, [academicYear, ids, workspaceId]);

  const cellQueryOptions = React.useMemo(() => {
    const options = [];
    for (const gradeId of ids) {
      for (const termNumber of [1, 2] as const) {
        options.push(
          annualTimetableCellsQueryOptions({
            workspaceId,
            academicYear,
            termNumber,
            gradeId,
            enabled: queryEnabled,
          })
        );
      }
    }
    return options;
  }, [academicYear, ids, queryEnabled, workspaceId]);

  const weekMemoQueryOptions = React.useMemo(
    () =>
      ([1, 2] as const).map((termNumber) =>
        annualTimetableWeekMemosQueryOptions({
          workspaceId,
          academicYear,
          termNumber,
          gradeIds: ids,
          enabled: queryEnabled && ids.length > 0,
        })
      ),
    [academicYear, ids, queryEnabled, workspaceId]
  );

  const cellQueries = useQueries({ queries: cellQueryOptions });
  const weekMemoQueries = useQueries({ queries: weekMemoQueryOptions });

  const cellsByKey = React.useMemo(() => {
    if (!workspaceId.trim() || ids.length === 0) return {};

    let merged: Record<string, AnnualTimetableCell> = {};
    let queryIndex = 0;
    for (const gradeId of ids) {
      for (const termNumber of [1, 2] as const) {
        const q = cellQueries[queryIndex];
        queryIndex += 1;
        const queryRows = Array.isArray(q?.data) ? q.data : [];
        const networkMap = queryRows.length > 0 ? buildAnnualCellMap(queryRows) : {};
        const termMerged = resolveMergedTimetableCellsByKey({
          workspaceId,
          academicYear,
          termNumber,
          gradeIds: [gradeId],
          networkCellsByKey: networkMap,
        });
        merged = { ...merged, ...termMerged };
      }
    }
    return merged;
  }, [academicYear, cellQueries, ids, workspaceId]);

  const weekMemosByKey = React.useMemo(
    () => buildWeekMemosStateMap(weekMemoQueries[0]?.data ?? [], weekMemoQueries[1]?.data ?? []),
    [weekMemoQueries]
  );

  const hasCellData = Object.keys(cellsByKey).length > 0;
  const cellQueriesPending = cellQueries.some((q) => q.isPending || q.isFetching);
  const weekMemosPending = weekMemoQueries.some((q) => q.isPending);

  const loading =
    queryEnabled && ids.length > 0 && (cellQueriesPending || weekMemosPending) && !hasCellData;

  const isReady =
    !queryEnabled ||
    ids.length === 0 ||
    hasCellData ||
    (cellQueries.length > 0 &&
      cellQueries.every((q) => q.isSuccess || q.isError) &&
      weekMemoQueries.every((q) => q.isSuccess || q.isError));

  const error = React.useMemo(() => {
    for (const q of [...cellQueries, ...weekMemoQueries]) {
      if (q.error) {
        return q.error instanceof Error ? q.error.message : String(q.error);
      }
    }
    return null;
  }, [cellQueries, weekMemoQueries]);

  return {
    scopeKey,
    cellsByKey,
    weekMemosByKey,
    loading,
    isReady,
    error,
  };
}
